import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { formatTransactionAmount } from '../utils/currency';
import { 
  ArrowLeft,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
  Filter
} from 'lucide-react';

export default function TransactionsPage() {
  const { t } = useLanguage();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const fetchTransactions = useCallback(async (currentFilter) => {
    setLoading(true);
    try {
      const params = currentFilter === 'all' ? {} : { tx_type: currentFilter };
      const response = await api.get('/transactions/', { params: { ...params, limit: 100 } });
      setTransactions(response.data.transactions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      logger.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchTransactions(filter);
  }, [filter, fetchTransactions]);

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'topup':
      case 'transfer_in':
      case 'refund':
        return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
      case 'transfer_out':
      case 'withdraw':
      case 'payment':
        return <ArrowUpRight className="h-4 w-4 text-red-500" />;
      default:
        return <Wallet className="h-4 w-4" />;
    }
  };

  const getTransactionAmount = (tx) => {
    const isCredit = ['topup', 'transfer_in', 'refund'].includes(tx.type);
    return (
      <span className={`font-semibold ${isCredit ? 'text-green-600' : 'text-red-500'}`}>
        {formatTransactionAmount(tx.amount, isCredit)}
      </span>
    );
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      topup: t('transactions.topup'),
      transfer_in: t('transactions.transfer_in'),
      transfer_out: t('transactions.transfer_out'),
      withdraw: t('transactions.withdraw'),
      payment: t('transactions.payment'),
      refund: 'Refund'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filterTabs = [
    { value: 'all', label: t('transactions.all') },
    { value: 'topup', label: t('transactions.topup') },
    { value: 'transfer_in', label: t('transactions.transfer_in') },
    { value: 'transfer_out', label: t('transactions.transfer_out') },
    { value: 'withdraw', label: t('transactions.withdraw') },
    { value: 'payment', label: t('transactions.payment') }
  ];

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <Button 
          variant="ghost" 
          className="mb-4" 
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('dashboard.welcome').split(',')[0]}
        </Button>

        <Card className="animate-fade-in-up">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                {t('transactions.title')}
              </CardTitle>
              <span className="text-sm text-muted-foreground">{total} transactions</span>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter Tabs */}
            <div className="mb-6 overflow-x-auto pb-2">
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="inline-flex w-auto">
                  {filterTabs.map(tab => (
                    <TabsTrigger 
                      key={tab.value} 
                      value={tab.value}
                      className="text-xs sm:text-sm whitespace-nowrap"
                      data-testid={`filter-${tab.value}`}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {/* Transactions List */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between py-4 border-b border-border/50">
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div>
                        <Skeleton className="h-4 w-40 mb-2" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-20" />
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Wallet className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">{t('transactions.no_transactions')}</p>
              </div>
            ) : (
              <div className="space-y-1">
                {transactions.map((tx) => (
                  <div 
                    key={tx.transaction_id} 
                    className="flex items-center justify-between py-4 border-b border-border/50 last:border-0 hover:bg-muted/30 -mx-4 px-4 rounded-lg transition-colors"
                    data-testid={`transaction-${tx.transaction_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium line-clamp-1">{tx.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <span>{formatDate(tx.created_at)}</span>
                          <span>•</span>
                          <span>{getTransactionTypeLabel(tx.type)}</span>
                          {tx.reference && (
                            <>
                              <span>•</span>
                              <span className="font-mono">{tx.reference}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <div className="text-lg">{getTransactionAmount(tx)}</div>
                      <div className="mt-1">{getStatusBadge(tx.status)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <MobileNav />
    </div>
  );
}
